import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import { SITE_URL } from "../lib/site";
import { TextQuoteButton } from "../components/home/TextQuoteButton";
import { PriceFactors } from "../components/home/PriceFactors";
import { WarrantySection } from "../components/home/WarrantySection";
import { FinalCta } from "../components/home/OfferReviews";
import { PhotoQuoteSection } from "../components/home/PhotoQuoteForm";
import { CompareSlider } from "../components/CompareSlider";
import { useContent } from "../lib/content";


const INCLUDED = [
  "Samsung Frame or LG G-series TV install",
  "Strong VersaBox Pro black in-wall box included",
  "Wires hidden where the wall allows",
  "Every Frame install gets the Black Box. Wires in, nothing showing.",
];

const FRAME_BODY = "A Frame TV should look like art on the wall, not like a TV with cables hanging. Set Up Home Theater installs Samsung Frame TVs across Rocky Mount and Eastern North Carolina with the flush, picture-frame finish: the TV sits flat on the wall, the One Connect box hides in the wall, and no wires show. Gallery install $549 includes the Strong VersaBox Pro black in-wall box, wire concealment where the wall allows, and the full setup. We sell the TV, the mount, and the installation as one bundle, one price, one visit, or we install a Frame TV you already own. Veteran owned, installing TVs since 2003, with a 5-year install labor warranty. Text a photo or video of your room to (252) 545-0011 for a fast quote.";

const FRAME_FAQS = [
  { q: "How much does Frame TV installation cost?", a: "Gallery install $549, which includes the VersaBox in-wall box and wire concealment where the wall allows. The TV and mount are quoted separately when you buy the bundle from us." },
  { q: "Will there be wires showing?", a: "No. The One Connect box goes in the wall and the wires run concealed, so the TV sits flat like a picture frame." },
  { q: "Do you install a Frame TV I already bought?", a: "Yes. We install customer-owned Frame TVs as well as TVs purchased through us." },
  { q: "How long does it take?", a: "Most Frame installs are completed in one visit." },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org", "@type": "FAQPage",
  mainEntity: FRAME_FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
};

const buildSchema = (price) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Frame and G-Series TV Installation (Gallery Install)",
  serviceType: "TV installation",
  provider: { "@id": `${SITE_URL}/#business` },
  areaServed: ["Rocky Mount", "Tarboro", "Wilson", "Nashville", "Zebulon", "Raleigh"].map((c) => ({ "@type": "City", name: `${c}, NC` })),
  offers: { "@type": "Offer", price: String(price), priceCurrency: "USD", description: "Flat-rate Gallery install, Strong VersaBox Pro black in-wall box included" },
});

export default function GalleryInstall() {
  const c = useContent();
  const g = c.prices.gallery;
  const FRAME_PHOTOS = c.photos.gallery;
  return (
    <main data-testid="gallery-install-page">
      <SEO
        title="Samsung Frame TV Installation in Rocky Mount, NC"
        description="Samsung Frame TV installation in Rocky Mount, NC. Flush, picture-frame finish with no wires showing, installed in one visit by a veteran-owned local installer since 2003. Call (252) 545-0011."
        path="/frame-tv-installation"
        jsonLd={[buildSchema(g), FAQ_SCHEMA]}
      />
      <section className="bg-[#06142e] text-white">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-20 md:py-24">
          <p className="text-[#3b9dff] font-semibold tracking-widest uppercase text-xs">Gallery install</p>
          <h1 className="mt-4 max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05]">Samsung Frame TV Installation in Rocky Mount, NC</h1>
          <p data-testid="gallery-price" className="mt-6 text-3xl font-extrabold text-[#3b9dff]">${g} flat-rate install</p>
          <p className="mt-3 max-w-2xl text-lg text-slate-300">Wires in, nothing showing. Serving Rocky Mount, Tarboro, Wilson, Nashville, Zebulon, and Raleigh.</p>
          <TextQuoteButton testId="gallery-text-quote-btn" label="Text a photo of your wall for a quote, (252) 545-0011." className="mt-10" />
        </div>
      </section>
      <section className="section-pad bg-white">
        <div className="max-w-[1000px] mx-auto px-5 lg:px-8">
          <p data-testid="frame-body" className="mb-12 text-lg text-slate-700 leading-relaxed">{FRAME_BODY}</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0a1f44]">What the Gallery install includes</h2>
          <ul className="mt-8 grid gap-3">
            {INCLUDED.map((t) => (
              <li key={t} className="flex gap-3 rounded-2xl border border-slate-200 p-5 text-slate-700"><Check className="w-5 h-5 mt-0.5 shrink-0 text-[#3b9dff]" />{t}</li>
            ))}
          </ul>
          <p className="mt-6 text-slate-600">The TV is quoted separately by text or call. Buying a TV and want the whole job done in one visit?{" "}
            <Link to="/" data-testid="gallery-home-offer-link" className="inline-flex items-center gap-1 font-semibold text-[#3b9dff] hover:underline">See the offer <ArrowRight className="w-4 h-4" /></Link>
          </p>
        </div>
      </section>
      <section data-testid="frame-before-after" className="pb-16 bg-white">
        <div className="max-w-[760px] mx-auto px-5 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0a1f44]">Before and after</h2>
          <p className="mt-2 mb-6 text-slate-600">Drag the slider. Before: the Strong VersaBox Pro in-wall box and mount. After: the Frame on the wall, wires in, nothing showing.</p>
          <CompareSlider before={FRAME_PHOTOS[0].src} after={FRAME_PHOTOS[1].src}
            beforeAlt="Strong VersaBox Pro in-wall box and Frame mount before the TV goes up" afterAlt="Finished Frame TV install above the mantel with no wires showing" />
        </div>
      </section>
      <section data-testid="frame-photos" className="pb-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 grid sm:grid-cols-3 gap-5">
          {FRAME_PHOTOS.map((p, i) => (
            <figure key={i} data-testid={`frame-photo-${i + 1}`}>
              <img src={p.src} alt={p.cap} loading="lazy" className="w-full aspect-square object-cover rounded-2xl" />
              <figcaption className="mt-2 text-sm text-slate-500">{p.cap}</figcaption>
            </figure>
          ))}
        </div>
      </section>
      <section data-testid="frame-faq" className="pb-20 bg-white">
        <div className="max-w-[860px] mx-auto px-5 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0a1f44] mb-8">Frame TV installation FAQs</h2>
          <div className="space-y-4">
            {FRAME_FAQS.map((f, i) => (
              <details key={i} data-testid={`frame-faq-${i}`} className="group bg-white rounded-xl border border-slate-200 p-5 open:shadow-md transition-shadow">
                <summary className="flex items-start justify-between gap-4 cursor-pointer list-none font-bold text-[#0a1f44]">
                  <span>{f.q}</span>
                  <span className="text-[#3b9dff] text-xl leading-none shrink-0 transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="text-slate-600 leading-relaxed text-sm mt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <PhotoQuoteSection source="gallery" />
      <PriceFactors />
      <WarrantySection />
      <FinalCta />
    </main>
  );
}
